<template>
  <div class="page-container">
    <PageHeader title="报告包申请记录">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增申请</el-button>
      </template>
    </PageHeader>

    <!-- 搜索面板：联动下拉 + 状态筛选 + 关键词 -->
    <SearchPanel>
      <el-select v-model="query.rrmaCustomer" clearable placeholder="客户群体" size="small" @change="handleSearchCustomerChange">
        <el-option v-for="item in searchCustomerOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaProvince" clearable placeholder="省份" size="small" :disabled="!query.rrmaCustomer" @change="handleSearchProvinceChange">
        <el-option v-for="item in searchProvinceOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaCity" clearable placeholder="地市" size="small" :disabled="!query.rrmaProvince" @change="handleSearchCityChange">
        <el-option v-for="item in searchCityOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaCourseType" clearable placeholder="课程类型" size="small" :disabled="!query.rrmaCity" @change="handleSearch">
        <el-option v-for="item in searchCourseTypeOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaStatus" clearable placeholder="申请状态" size="small" @change="handleSearch">
        <el-option label="待提交" :value="1"></el-option>
        <el-option label="待审核" :value="2"></el-option>
        <el-option label="已发包" :value="3"></el-option>
      </el-select>
      <el-input v-model="query.keySearch" clearable placeholder="关键词搜索" size="small" style="width: 200px" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <div class="apply-status-tabs" v-loading="statusGroupLoading">
      <button
        v-for="item in statusTabs"
        :key="item.status"
        type="button"
        class="apply-status-tabs__item"
        :class="{ 'is-active': item.active }"
        @click="handleStatusTabClick(item)"
      >
        <span>{{ item.label }}</span>
        <strong>（{{ item.value }}）</strong>
      </button>
    </div>

    <!-- 列表 -->
    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="rrmaId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      >
        <template #cell-rrmaStatus="{ row }">
          <span :style="{ color: statusColorMap[row.rrmaStatus] || '#909399', fontWeight: '500' }">{{ statusMap[row.rrmaStatus] || '未知' }}</span>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无申请记录" icon="el-icon-document-checked"></EmptyState>
    </TablePanel>

    <!-- 新增弹窗 -->
    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="addFormConfig"
      :form-data="addFormData"
      dialog-title="新增申请"
      dialog-width="80%"
      :confirm-loading="submitLoading"
      @confirm="handleAddConfirm"
      @item-change="handleFormItemChange('add', $event)"
    >
      <template #footer="{ confirm, cancel }">
        <el-button size="small" @click="cancel">取消</el-button>
        <el-button size="small" :loading="submitLoading" @click="addFormData.rrmaStatus = 1; addSubmitType = 1; confirm()">保存</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="addFormData.rrmaStatus = 2; addSubmitType = 2; confirm()">提交</el-button>
      </template>
    </aj-form-add>

    <!-- 编辑弹窗 -->
    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="editFormConfig"
      :form-data="editFormData"
      dialog-title="编辑申请"
      dialog-width="80%"
      :confirm-loading="submitLoading"
      @confirm="handleEditSubmit"
      @item-change="handleFormItemChange('edit', $event)"
    >
      <template #footer="{ confirm, cancel }">
        <el-button size="small" @click="cancel">取消</el-button>
        <el-button size="small" :loading="submitLoading" @click="editFormData.rrmaStatus = 1; editSubmitType = 1; confirm()">保存</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="editFormData.rrmaStatus = 2; editSubmitType = 2; confirm()">提交</el-button>
      </template>
    </aj-form-edit>

    <!-- 查看详情弹窗 -->
    <el-dialog v-dialog-drag-resize :visible.sync="viewDialogVisible" width="80%" top="7vh" custom-class="detail-dialog" :close-on-click-modal="false" :show-close="false">
      <template #title>
        <div class="detail-dialog-header">
          <div class="detail-dialog-title-row">
            <span class="detail-dialog-title">申请记录详情</span>
            <span v-if="viewData" class="detail-status-tag" :class="'detail-status--' + viewData.rrmaStatus">
              {{ statusMap[viewData.rrmaStatus] || '未知' }}
            </span>
          </div>
          <i class="dialog-close-btn" @click="viewDialogVisible = false" />
        </div>
      </template>
      <div class="detail-content" v-if="viewData">

        <!-- 基本信息 -->
        <div class="detail-section-title">
          <i class="el-icon-office-building"></i>
          <span>基本信息</span>
        </div>
        <div class="detail-table detail-table--2col">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">销售订单号</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.rfiOrderNum || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">客户群体</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">省份</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaProvince || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">地市</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCity || '-' }}</div>
            <div class="detail-cell detail-cell--label">课程类型</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCourseType || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">包内含资料</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.rrmaModelName || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">客户名称</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">客户简称</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameCustomerShort || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">课程名称</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.applyRuleNameCourse || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">开始日期</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameDateStart || '-' }}</div>
            <div class="detail-cell detail-cell--label">结束日期</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameDateEnd || '-' }}</div>
          </div>
        </div>

        <!-- 审核信息 -->
        <div class="detail-section-title">
          <i class="el-icon-s-check"></i>
          <span>审核信息</span>
        </div>
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">申请人</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyusername || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">申请时间</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applytime || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核人</div>
            <div class="detail-cell detail-cell--value">{{ viewData.checkusername || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核时间</div>
            <div class="detail-cell detail-cell--value">{{ viewData.checktime || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核备注</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ viewData.rrmaStatusDes || '-' }}</div>
          </div>
        </div>

        <!-- 资料地址（已发包状态才展示） -->
        <template v-if="viewData.rrmaStatus === 3 && viewData.rrmaModelPath">
          <div class="detail-section-title">
            <i class="el-icon-folder-opened"></i>
            <span>资料下载</span>
          </div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-cell detail-cell--label">资料地址</div>
              <div class="detail-cell detail-cell--value">
                <div class="detail-file-list">
                  <span v-for="(url, idx) in viewData.rrmaModelPath.split(',').filter(Boolean)" :key="idx" class="detail-file-chip detail-file-preview" @click="openFilePreview(url)">
                    <i class="el-icon-view"></i>
                    <span>{{ viewData.rrmaModelName || url.split('/').pop() }}</span>
                  </span>
                  <el-button type="text" size="mini" icon="el-icon-download" class="detail-download-btn" @click="downloadToLocal(viewData.rrmaModelPath.split(',')[0])">保存到电脑</el-button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 文件命名规范 -->
        <template v-if="viewNamingRules.length">
          <div class="detail-section-title">
            <i class="el-icon-document-copy"></i>
            <span>文件命名规范（可直接复制使用）</span>
          </div>
          <table class="naming-table">
            <thead>
              <tr>
                <th>文件类型</th>
                <th>命名规则</th>
                <th>示例</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(rule, idx) in viewNamingRules" :key="idx">
                <td>{{ rule.fileType || '-' }}</td>
                <td>{{ rule.nameRule || '-' }}</td>
                <td class="naming-td-example">
                  <span>{{ rule.example || '-' }}</span>
                  <el-button
                    v-if="rule.example"
                    type="text"
                    size="mini"
                    icon="el-icon-document-copy"
                    class="example-copy-btn"
                    @click="handleCopyText(rule.example)"
                  >复制</el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>

      </div>
      <template #footer>
        <el-button v-if="viewData && viewData.rrmaStatus === 3" type="primary" size="small" icon="el-icon-document" @click="handleGenerateFromDetail">报告生成</el-button>
        <el-button size="small" @click="viewDialogVisible = false">关 闭</el-button>
      </template>
    </el-dialog>

    <!-- 报告生成弹框 -->
    <report-generate :visible.sync="generateDialogVisible" :rrma-id="generateRrmaId" width="90%" />

    <!-- 文件预览 -->
    <FilePreview :visible.sync="previewVisible" :url="previewFileUrl" />
  </div>
</template>

<script>
import {
  getRtReportModelApplyListUser,
  getRtReportModelListParamUser,
  getRtReportModelApplyDetailsUser,
  addRtReportModelApplyUser,
  updateRtReportModelApplyUser,
  submitRtReportModelApplyUser,
  deleteRtReportModelApplyUser,
  getRtFxxkItineraryListApplyUsers,
  getRtReportModelApplyStatusGroupUser,
  normalizeApplyPageResponse
} from '@/api/api'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import FilePreview from '@/components/FilePreview.vue'
import ReportGenerate from './reportGenerate.vue'

// 状态码映射
const STATUS_MAP = { 1: '待提交', 2: '待审核', 3: '已发包' }
const STATUS_GROUP_TABS = [
  { label: '全部申请', status: 0 },
  { label: '待提交', status: 1 },
  { label: '待审核', status: 2 },
  { label: '已发包', status: 3 }
]

// 字符串数组转 {label, value} 选项格式（ajFormBase select 组件要求）
function toOptions (list) {
  return (list || []).map(item => ({ label: item, value: item }))
}

// 行程表记录数组转销售订单号下拉选项（去重）
function toOrderNumOptions (list) {
  const seen = new Set()
  return (list || []).filter(item => {
    if (!item.rfiOrderNum || seen.has(item.rfiOrderNum)) return false
    seen.add(item.rfiOrderNum)
    return true
  }).map(item => ({ label: item.rfiProjectName, value: item.rfiOrderNum }))
}

function createEmptyFormData () {
  return {
    rrmaId: null,
    rfiOrderNum: '',
    rrmaCustomer: '',
    rrmaProvince: '',
    rrmaCity: '',
    rrmaCourseType: '',
    rrmaModelName: '',
    rrmaStatus: 1,
    applyRuleNameCustomer: '',
    applyRuleNameCustomerShort: '',
    applyRuleNameCourse: '',
    applyRuleNameDateStart: '',
    applyRuleNameDateEnd: ''
  }
}

export default {
  name: 'ReportApplyListPage',
  components: { AjFormAdd, AjFormEdit, AjTable, EmptyState, FilePreview, PageHeader, SearchPanel, TablePanel, ReportGenerate },
  data () {
    return {
      loading: false,
      submitLoading: false,
      statusGroupLoading: false,
      statusGroupRows: [],
      addSubmitType: 1,
      list: [],
      total: 0,
      statusMap: STATUS_MAP,
      statusColorMap: { 1: '#f59e0b', 2: '#4f7cff', 3: '#10b981' },

      // 行程表记录列表（页面初始化时加载一次，弹窗共用）
      itineraryList: [],

      // 搜索条件
      query: {
        keySearch: '',
        rrmaCustomer: '',
        rrmaProvince: '',
        rrmaCity: '',
        rrmaCourseType: '',
        rrmaStatus: '',
        currentPage: 1,
        pageSize: 10
      },
      searchCustomerOptions: [],
      searchProvinceOptions: [],
      searchCityOptions: [],
      searchCourseTypeOptions: [],

      // 新增弹窗
      addDialogVisible: false,
      addFormData: createEmptyFormData(),
      addOrderNumOptions: [],
      orderNumLoading: false,
      addCustomerOptions: [],
      addProvinceOptions: [],
      addCityOptions: [],
      addCourseTypeOptions: [],
      addModelNameOptions: [],

      // 编辑弹窗
      editDialogVisible: false,
      editSubmitType: 1,
      editFormData: createEmptyFormData(),
      editOrderNumOptions: [],
      editCustomerOptions: [],
      editProvinceOptions: [],
      editCityOptions: [],
      editCourseTypeOptions: [],
      editModelNameOptions: [],

      // 查看弹窗
      viewDialogVisible: false,
      viewData: null,

      // 文件预览
      previewVisible: false,
      previewFileUrl: '',
      viewNamingRules: [],

      // 报告生成弹框
      generateDialogVisible: false,
      generateRrmaId: null
    }
  },
  computed: {
    statusTabs () {
      return STATUS_GROUP_TABS.map(item => {
        const queryStatus = this.query.rrmaStatus === '' ? 0 : Number(this.query.rrmaStatus)
        const matched = this.statusGroupRows.find(row => Number(row.rrmaStatus) === item.status)
        return {
          ...item,
          value: Number((matched && matched.statusNum) || 0),
          active: queryStatus === item.status
        }
      })
    },
    // 表格列配置
    columns () {
      return [
        { type: 'index', label: '序号', width: 60 },
        { prop: 'rfiOrderNum', label: '销售订单号', width: 140, showTooltip: true },
        { prop: 'rrmaCustomer', label: '客户群体', minWidth: 120 },
        { prop: 'rrmaProvince', label: '省份', width: 100 },
        { prop: 'rrmaCity', label: '地市', width: 100 },
        { prop: 'rrmaCourseType', label: '课程类型', width: 120 },
        { prop: 'rrmaModelName', label: '包内含资料', minWidth: 300 },
        {
          prop: 'rtReportModel',
          label: '关联模板',
          minWidth: 480,
          render: (h, { row }) => {
            const model = row.rtReportModel
            if (!model) return h('span', { style: { color: '#909399' } }, '-')
            // 提取三个关联模板，带类型前缀区分
            const templates = [
              { label: '签到', name: model.relationRrmfIdOneName },
              { label: '评分', name: model.relationRrmfIdTwoName },
              { label: '总结', name: model.relationRrmfIdThreeName }
            ].filter(t => t.name)
            if (!templates.length) return h('span', { style: { color: '#909399' } }, '-')
            return h('div', templates.map(t =>
              h('span', {
                class: ['related-model-tag', 'related-model-tag--' + t.label],
                key: t.label
              }, [
                h('span', { class: 'related-model-tag__type' }, t.label),
                h('span', { class: 'related-model-tag__name' }, t.name)
              ])
            ))
          }
        },
        { prop: 'applyusername', label: '申请人', width: 100 },
        {
          prop: 'rrmaStatus',
          label: '状态',
          width: 90,
          render: (h, { row }) => {
            const text = STATUS_MAP[row.rrmaStatus] || '未知'
            const colorMap = { 1: '#f59e0b', 2: '#4f7cff', 3: '#10b981' }
            return h('span', { style: { color: colorMap[row.rrmaStatus] || '#909399', fontWeight: '500' } }, text)
          }
        },
        { prop: 'applytime', label: '申请时间', width: 160 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 280,
          fixed: 'right',
          buttons: (row) => {
            const btns = [{ key: 'view', text: '查看', type: 'text' }]
            if (row.rrmaStatus === 1) {
              btns.push({ key: 'submit', text: '提交', type: 'text' })
              btns.push({ key: 'edit', text: '编辑', type: 'text' })
              btns.push({ key: 'delete', text: '删除', type: 'text', class: 'text-danger' })
            }
            if (row.rrmaStatus === 2) {
              btns.push({ key: 'workflow', text: '查看进度', type: 'text' })
            }
            if (row.rrmaStatus === 3) {
              btns.push({ key: 'generate', text: '继续生成', type: 'text' })
            }
            return btns
          }
        }
      ]
    },
    // 新增表单配置
    addFormConfig () {
      return [
        [
          { label: '销售订单号', prop: 'rfiOrderNum', type: 'select', span: 24, style: { width: '100%' }, filterable: true, remote: true, remoteMethod: this.remoteSearchOrderNum, options: this.addOrderNumOptions, clearable: true, placeholder: '输入行程名称搜索订单号' }
        ],
        [
          { label: '客户群体', prop: 'rrmaCustomer', type: 'select', span: 12, required: true, options: this.addCustomerOptions, clearable: true },
          { label: '省份', prop: 'rrmaProvince', type: 'select', span: 12, required: true, options: this.addProvinceOptions, clearable: true, disabled: !this.addFormData.rrmaCustomer }
        ],
        [
          { label: '地市', prop: 'rrmaCity', type: 'select', span: 12, options: this.addCityOptions, clearable: true, disabled: !this.addFormData.rrmaProvince },
          { label: '课程类型', prop: 'rrmaCourseType', type: 'select', span: 12, required: true, options: this.addCourseTypeOptions, clearable: true, disabled: !this.addFormData.rrmaCity }
        ],
        [
          { label: '包内含资料', prop: 'rrmaModelName', style: { width: '95%' }, type: 'select', span: 24, required: true, options: this.addModelNameOptions, clearable: true, disabled: !this.addFormData.rrmaCourseType }
        ],
        [
          { label: '客户名称', prop: 'applyRuleNameCustomer', type: 'input', span: 12 },
          { label: '客户简称', prop: 'applyRuleNameCustomerShort', type: 'input', span: 12 }
        ],
        [
          { label: '课程名称', prop: 'applyRuleNameCourse', type: 'input', span: 24 }
        ],
        [
          { label: '开始日期', prop: 'applyRuleNameDateStart', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 },
          { label: '结束日期', prop: 'applyRuleNameDateEnd', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 }
        ]
      ]
    },
    // 编辑表单配置
    editFormConfig () {
      return [
        [
          { label: '销售订单号', prop: 'rfiOrderNum', type: 'select', span: 24, style: { width: '100%' }, filterable: true, remote: true, remoteMethod: this.remoteSearchOrderNum, options: this.editOrderNumOptions, clearable: true, placeholder: '输入行程名称搜索订单号' }
        ],
        [
          { label: '客户群体', prop: 'rrmaCustomer', type: 'select', span: 12, required: true, options: this.editCustomerOptions, clearable: true },
          { label: '省份', prop: 'rrmaProvince', type: 'select', span: 12, required: true, options: this.editProvinceOptions, clearable: true, disabled: !this.editFormData.rrmaCustomer }
        ],
        [
          { label: '地市', prop: 'rrmaCity', type: 'select', span: 12, options: this.editCityOptions, clearable: true, disabled: !this.editFormData.rrmaProvince },
          { label: '课程类型', prop: 'rrmaCourseType', type: 'select', span: 12, required: true, options: this.editCourseTypeOptions, clearable: true, disabled: !this.editFormData.rrmaCity }
        ],
        [
          { label: '包内含资料', prop: 'rrmaModelName', style: { width: '95%' }, type: 'select', span: 24, required: true, options: this.editModelNameOptions, clearable: true, disabled: !this.editFormData.rrmaCourseType }
        ],
        [
          { label: '客户名称', prop: 'applyRuleNameCustomer', type: 'input', span: 12 },
          { label: '客户简称', prop: 'applyRuleNameCustomerShort', type: 'input', span: 12 }
        ],
        [
          { label: '课程名称', prop: 'applyRuleNameCourse', type: 'input', span: 24 }
        ],
        [
          { label: '开始日期', prop: 'applyRuleNameDateStart', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 },
          { label: '结束日期', prop: 'applyRuleNameDateEnd', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 }
        ]
      ]
    }
  },
  created () {
    this.fetchList()
    this.loadStatusGroup()
    this.loadSearchCustomerOptions()
    this.remoteSearchOrderNum('')
  },
  methods: {
    // ============ 远程搜索订单号（空关键词加载默认列表） ============
    async remoteSearchOrderNum (query) {
      this.orderNumLoading = true
      try {
        const params = {}
        if (query && query.trim()) params.afiProjectName = query.trim()
        const res = await getRtFxxkItineraryListApplyUsers(params)
        this.itineraryList = res.data || []
        const options = toOrderNumOptions(res.data)
        this.addOrderNumOptions = options
        this.editOrderNumOptions = options
      } catch (e) {
        this.itineraryList = []
      } finally {
        this.orderNumLoading = false
      }
    },

    // ============ 列表查询 ============
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.keySearch) params.keySearch = this.query.keySearch
        if (this.query.rrmaCustomer) params.rrmaCustomer = this.query.rrmaCustomer
        if (this.query.rrmaProvince) params.rrmaProvince = this.query.rrmaProvince
        if (this.query.rrmaCity) params.rrmaCity = this.query.rrmaCity
        if (this.query.rrmaCourseType) params.rrmaCourseType = this.query.rrmaCourseType
        if (this.query.rrmaStatus) params.rrmaStatus = this.query.rrmaStatus
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getRtReportModelApplyListUser(params)
        const pageData = normalizeApplyPageResponse(res)
        this.list = pageData.list
        this.total = pageData.total
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadStatusGroup () {
      this.statusGroupLoading = true
      try {
        const res = await getRtReportModelApplyStatusGroupUser({})
        this.statusGroupRows = Array.isArray(res && res.data) ? res.data : []
      } catch (error) {
        this.statusGroupRows = []
      } finally {
        this.statusGroupLoading = false
      }
    },
    handleStatusTabClick (item) {
      const nextStatus = item.status === 0 ? '' : item.status
      if (this.query.rrmaStatus === nextStatus) return
      this.query.rrmaStatus = nextStatus
      this.handleSearch()
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { keySearch: '', rrmaCustomer: '', rrmaProvince: '', rrmaCity: '', rrmaCourseType: '', rrmaStatus: '', currentPage: 1, pageSize: 10 }
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      this.fetchList()
      this.loadStatusGroup()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // ============ 搜索面板联动 ============
    async loadSearchCustomerOptions () {
      try {
        const res = await getRtReportModelListParamUser({ modelType: 1 })
        this.searchCustomerOptions = res.data || []
      } catch (e) {
        this.searchCustomerOptions = []
      }
    },
    async handleSearchCustomerChange (val) {
      this.query.rrmaProvince = ''
      this.query.rrmaCity = ''
      this.query.rrmaCourseType = ''
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParamUser({ modelType: 2, rrmCustomer: val })
        this.searchProvinceOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchProvinceChange (val) {
      this.query.rrmaCity = ''
      this.query.rrmaCourseType = ''
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParamUser({ modelType: 3, rrmCustomer: this.query.rrmaCustomer, rrmProvince: val })
        this.searchCityOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchCityChange (val) {
      this.query.rrmaCourseType = ''
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParamUser({ modelType: 4, rrmCustomer: this.query.rrmaCustomer, rrmProvince: this.query.rrmaProvince, rrmCity: val })
        this.searchCourseTypeOptions = res.data || []
      } catch (e) {}
    },

    // ============ 表单联动 ============
    async loadFormOptions (modelType, rrmCustomer, rrmProvince, rrmCity, rrmCourseType) {
      const params = { modelType }
      if (rrmCustomer) params.rrmCustomer = rrmCustomer
      if (rrmProvince) params.rrmProvince = rrmProvince
      if (rrmCity) params.rrmCity = rrmCity
      if (rrmCourseType) params.rrmCourseType = rrmCourseType
      try {
        const res = await getRtReportModelListParamUser(params)
        return toOptions(res.data)
      } catch (e) {
        return []
      }
    },
    // 级联自动选中：选项只有一项时自动选中并继续加载下一级
    async autoCascade (mode, currentOptions, currentModelType) {
      if (!currentOptions || currentOptions.length !== 1 || currentModelType > 5) return
      const value = currentOptions[0].value
      const formData = mode === 'add' ? this.addFormData : this.editFormData
      const propMap = { 1: 'rrmaCustomer', 2: 'rrmaProvince', 3: 'rrmaCity', 4: 'rrmaCourseType', 5: 'rrmaModelName' }
      const formDataProp = propMap[currentModelType]
      if (!formDataProp) return
      formData[formDataProp] = value
      if (currentModelType >= 5) return
      // 清空下级字段
      for (let i = currentModelType + 1; i <= 5; i++) {
        formData[propMap[i]] = ''
      }
      const nextModelType = currentModelType + 1
      const nextOpts = await this.loadFormOptions(
        nextModelType,
        formData.rrmaCustomer,
        formData.rrmaProvince,
        formData.rrmaCity,
        formData.rrmaCourseType
      )
      const optionsMap = mode === 'add'
        ? { 2: 'addProvinceOptions', 3: 'addCityOptions', 4: 'addCourseTypeOptions', 5: 'addModelNameOptions' }
        : { 2: 'editProvinceOptions', 3: 'editCityOptions', 4: 'editCourseTypeOptions', 5: 'editModelNameOptions' }
      this[optionsMap[nextModelType]] = nextOpts
      for (let i = nextModelType + 1; i <= 5; i++) {
        this[optionsMap[i]] = []
      }
      await this.autoCascade(mode, nextOpts, nextModelType)
    },
    async handleFormItemChange (mode, { prop, value }) {
      const formData = mode === 'add' ? this.addFormData : this.editFormData
      if (prop === 'rfiOrderNum') {
        // 清空订单号时重置为默认列表
        if (!value) {
          formData.applyRuleNameCustomer = ''
          formData.applyRuleNameCustomerShort = ''
          formData.applyRuleNameCourse = ''
          formData.applyRuleNameDateStart = ''
          formData.applyRuleNameDateEnd = ''
          this.remoteSearchOrderNum('')
          return
        }
        // 选择销售订单号后，自动填充关联字段
        const selected = (this.itineraryList || []).find(item => item.rfiOrderNum === value)
        if (selected) {
          formData.applyRuleNameCustomer = selected.rfiProjectCustomer || ''
          formData.applyRuleNameCustomerShort = selected.rfiProjectCustomerShort || ''
          formData.applyRuleNameCourse = selected.rfiCourseName || ''
          formData.applyRuleNameDateStart = selected.rfiCourseDateStart || ''
          formData.applyRuleNameDateEnd = selected.rfiCourseDateEnd || ''
        } else {
          formData.applyRuleNameCustomer = ''
          formData.applyRuleNameCustomerShort = ''
          formData.applyRuleNameCourse = ''
          formData.applyRuleNameDateStart = ''
          formData.applyRuleNameDateEnd = ''
        }
      } else if (prop === 'rrmaCustomer') {
        formData.rrmaProvince = ''
        formData.rrmaCity = ''
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        const opts = await this.loadFormOptions(2, value, '', '')
        if (mode === 'add') {
          this.addProvinceOptions = opts
          this.addCityOptions = []
          this.addCourseTypeOptions = []
          this.addModelNameOptions = []
        } else {
          this.editProvinceOptions = opts
          this.editCityOptions = []
          this.editCourseTypeOptions = []
          this.editModelNameOptions = []
        }
        await this.autoCascade(mode, opts, 2)
      } else if (prop === 'rrmaProvince') {
        formData.rrmaCity = ''
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        const opts = await this.loadFormOptions(3, formData.rrmaCustomer, value, '')
        if (mode === 'add') {
          this.addCityOptions = opts
          this.addCourseTypeOptions = []
          this.addModelNameOptions = []
        } else {
          this.editCityOptions = opts
          this.editCourseTypeOptions = []
          this.editModelNameOptions = []
        }
        await this.autoCascade(mode, opts, 3)
      } else if (prop === 'rrmaCity') {
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        const opts = await this.loadFormOptions(4, formData.rrmaCustomer, formData.rrmaProvince, value)
        if (mode === 'add') {
          this.addCourseTypeOptions = opts
          this.addModelNameOptions = []
        } else {
          this.editCourseTypeOptions = opts
          this.editModelNameOptions = []
        }
        await this.autoCascade(mode, opts, 4)
      } else if (prop === 'rrmaCourseType') {
        formData.rrmaModelName = ''
        const opts = await this.loadFormOptions(5, formData.rrmaCustomer, formData.rrmaProvince, formData.rrmaCity, value)
        if (mode === 'add') {
          this.addModelNameOptions = opts
        } else {
          this.editModelNameOptions = opts
        }
        await this.autoCascade(mode, opts, 5)
      } else if (prop === 'rrmaStatus') {
        if (mode === 'add') {
          this.addSubmitType = value || 1
        } else {
          this.editSubmitType = value || 1
        }
      }
    },

    goWorkflow (mode, row = {}) {
      const query = { mode }
      if (row.rrmaId) query.rrmaId = row.rrmaId
      this.$router.push({ path: '/reportApply/reportApplyWorkflow', query }).catch(() => {})
    },

    // ============ 新增 ============
    async handleAdd () {
      this.goWorkflow('add')
    },
    async handleAddConfirm ({ data }) {
      this.submitLoading = true
      try {
        const submitType = this.addSubmitType || data.rrmaStatus || 1
        await addRtReportModelApplyUser({
          rfiOrderNum: data.rfiOrderNum,
          rrmaCustomer: data.rrmaCustomer,
          rrmaProvince: data.rrmaProvince,
          rrmaCity: data.rrmaCity,
          rrmaCourseType: data.rrmaCourseType,
          rrmaModelName: data.rrmaModelName,
          rrmaStatus: submitType,
          applyRuleNameCustomer: data.applyRuleNameCustomer,
          applyRuleNameCustomerShort: data.applyRuleNameCustomerShort,
          applyRuleNameCourse: data.applyRuleNameCourse,
          applyRuleNameDateStart: data.applyRuleNameDateStart,
          applyRuleNameDateEnd: data.applyRuleNameDateEnd
        })
        this.$message.success(submitType === 1 ? '申请已保存' : '申请已提交')
        this.addDialogVisible = false
        this.fetchList()
        this.loadStatusGroup()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 编辑 ============
    async handleEdit (row) {
      this.goWorkflow('edit', row)
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        await updateRtReportModelApplyUser({
          rrmaId: data.rrmaId,
          rfiOrderNum: data.rfiOrderNum,
          rrmaCustomer: data.rrmaCustomer,
          rrmaProvince: data.rrmaProvince,
          rrmaCity: data.rrmaCity,
          rrmaCourseType: data.rrmaCourseType,
          rrmaModelName: data.rrmaModelName,
          rrmaStatus: this.editSubmitType || data.rrmaStatus || 1,
          applyRuleNameCustomer: data.applyRuleNameCustomer,
          applyRuleNameCustomerShort: data.applyRuleNameCustomerShort,
          applyRuleNameCourse: data.applyRuleNameCourse,
          applyRuleNameDateStart: data.applyRuleNameDateStart,
          applyRuleNameDateEnd: data.applyRuleNameDateEnd
        })
        this.$message.success((this.editSubmitType || data.rrmaStatus) === 1 ? '申请已保存' : '申请已提交')
        this.editDialogVisible = false
        this.fetchList()
        this.loadStatusGroup()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 提交 ============
    async handleSubmit (row) {
      try {
        await this.$confirm('确认提交该申请？提交后将无法修改。', '提交确认', { type: 'warning' })
        await submitRtReportModelApplyUser({ rrmaId: row.rrmaId })
        this.$message.success('申请已提交，等待审核')
        this.fetchList()
        this.loadStatusGroup()
      } catch (error) {}
    },

    // ============ 删除 ============
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该申请记录？', '提示', { type: 'warning' })
        await deleteRtReportModelApplyUser({ rrmaId: row.rrmaId })
        this.$message.success('申请记录已删除')
        this.fetchList()
        this.loadStatusGroup()
      } catch (error) {}
    },

    // ============ 查看详情 ==========
    async handleView (row) {
      try {
        const res = await getRtReportModelApplyDetailsUser({ rrmaId: row.rrmaId })
        this.viewData = res.data || row
        // 提取文件命名规范（过滤掉表头行）
        this.viewNamingRules = Array.isArray(res.item)
          ? res.item.filter(r => r.fileType !== '文件类型')
          : []
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取申请详情失败')
      }
    },

    // ============ 操作按钮分发 ============
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'view': this.handleView(row); break
        case 'edit': this.handleEdit(row); break
        case 'submit': this.handleSubmit(row); break
        case 'delete': this.handleDelete(row); break
        case 'workflow': this.goWorkflow('edit', row); break
        case 'generate': this.handleGenerate(row); break
      }
    },

    // ============ 文件预览与下载 ============
    openFilePreview (url) {
      this.previewFileUrl = url
      this.previewVisible = true
    },
    // 下载资料文件到电脑（<a> 标签下载，不走 fetch，避免跨域）
    downloadToLocal (url) {
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      this.$message({ type: 'success', message: '文件已开始下载，可在浏览器下载栏（Ctrl+J）查看', duration: 5000 })
    },

    // ============ 报告生成 ============
    handleGenerate (row) {
      this.goWorkflow('generate', row)
    },
    // 从详情弹框触发报告生成
    handleGenerateFromDetail () {
      if (!this.viewData || !this.viewData.rrmaId) return
      this.viewDialogVisible = false
      this.goWorkflow('generate', this.viewData)
    },

    // 复制文本到剪贴板
    handleCopyText (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        // 降级方案
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message.success('已复制到剪贴板')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.text-danger { color: #ef4444 !important; }

// 关联模板标签（render 函数生成，需要 ::v-deep 穿透）
::v-deep .related-model-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  margin: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;

  &__type {
    display: inline-block;
    padding: 0 6px;
    margin-right: 6px;
    margin-left: -10px;
    line-height: 20px;
    font-weight: 600;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }

  &--签到 {
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    color: #0369a1;
    .related-model-tag__type { color: #0284c7; border-right-color: #bae6fd; }
  }
  &--评分 {
    background: #fefce8;
    border: 1px solid #fde68a;
    color: #a16207;
    .related-model-tag__type { color: #ca8a04; border-right-color: #fde68a; }
  }
  &--总结 {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #15803d;
    .related-model-tag__type { color: #16a34a; border-right-color: #bbf7d0; }
  }
}

/* ========== 详情弹框 ========== */
::v-deep .detail-dialog {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 0;
    max-height: 70vh;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  .el-dialog__footer {
    padding: 14px 28px;
    border-top: 1px solid #eef1f6;
  }
}

.detail-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 18px;
  border-bottom: 1px solid #eef1f6;
}

.detail-dialog-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}


.detail-content {
  padding: 20px 28px 24px;
}

// 状态标签
.detail-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.detail-status--1 {
  background: #fffbeb;
  color: #d97706;
}

.detail-status--2 {
  background: #eff6ff;
  color: #2563eb;
}

.detail-status--3 {
  background: #ecfdf5;
  color: #059669;
}

// 分区标题
.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding-left: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, #4f7cff, #6366f1);
    border-radius: 2px;
  }

  i {
    font-size: 16px;
    color: #4f7cff;
    margin-left: 4px;
  }

  & + .detail-table {
    margin-bottom: 24px;
  }
}

// 表格布局：标签灰底 + 值白底
.detail-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;

  &--2col {
    .detail-row {
      display: grid;
      grid-template-columns: repeat(2, auto 1fr);
    }
  }
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-cell {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;

  &--label {
    background: #f5f7fa;
    color: #606266;
    font-weight: 500;
    white-space: nowrap;
    min-width: 100px;
    width: 100px;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
  }

  &--value {
    background: #fff;
    color: #1e293b;
    flex: 1;
    word-break: break-all;

    &.detail-cell--pre {
      white-space: pre-wrap;
      color: #475569;
    }
  }
}

// 文件命名规范表格
.naming-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
  table-layout: fixed;

  th, td {
    padding: 10px 14px;
    font-size: 13px;
    text-align: center;
    border-bottom: 1px solid #e2e8f0;
    border-right: 1px solid #e2e8f0;
    word-break: break-all;
  }

  th:last-child, td:last-child {
    border-right: none;
  }

  th {
    background: #f5f7fa;
    font-weight: 600;
    color: #606266;
  }

  td {
    color: #1e293b;
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.naming-td-example {
  display: table-cell;

  span {
    vertical-align: middle;
  }
}

.example-copy-btn {
  margin-left: 8px;
  padding: 0 4px;
  font-size: 12px;
  color: #4f7cff;
  vertical-align: middle;
}

.example-copy-btn:hover {
  color: #2563eb;
}

// 资料文件列表
.detail-file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.detail-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: #f0f5ff;
  border: 1px solid #dce5f5;
  border-radius: 6px;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s;
  max-width: 220px;

  i { flex-shrink: 0; font-size: 13px; }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #e0ecff;
    color: #2563eb;
    text-decoration: none;
  }
}

.detail-file-preview {
  cursor: pointer;
  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.15);
  }
}
.detail-download-btn {
  color: #10b981;
  padding: 5px 8px;
  &:hover { color: #059669; }
}

// 操作链接
::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &.text-danger {
    color: #ef4444 !important;
    &:hover { color: #dc2626 !important; }
  }

  &:not(.text-danger):hover { color: #4f7cff !important; }
}
</style>
